import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HintBar from '@/components/HintBar';

const HINTS = [
  'Each particle = one sub-activity · Y position = automation opportunity',
  'Click a particle to inspect hours, bucket, and opportunity score',
  'Filter by activity station on the left to focus the spine',
  'Bigger particles = more hours · Higher up = more automatable',
];

/* ================================================================
   Analyze — Recipe 3.16 · The opportunity spine
   3-col: stations (240) · spine canvas (1fr) · inspector (300)
   ================================================================ */

const BUCKET_KEYS = ['bot', 'borrow', 'build', 'bi', 'buy'];
const BUCKET_LABEL = {
  bot:    'Bot',
  borrow: 'Borrow',
  build:  'Build',
  bi:     'BI',
  buy:    'Buy',
};

const STEPS = [
  { token: '✓',  label: 'Ingest',      path: '/analysis',    done: true },
  { token: '✓',  label: 'Deconstruct', path: '/deconstruct', done: true },
  { token: '03', label: 'Analyze',     path: '/analyze',     active: true },
  { token: '04', label: 'Sort · 4B',   path: '/sort' },
  { token: '05', label: 'Recompose',   path: '/recompose' },
];

/**
 * Read resolved 4B bucket colors from CSS custom properties so canvas
 * fill styles track tokens.css without being duplicated here.
 */
function readBucketColors() {
  const root = document.documentElement;
  const cs = getComputedStyle(root);
  const pick = (name, fallback) => (cs.getPropertyValue(name).trim() || fallback);
  return {
    bot:    pick('--bot',    '#00D4C8'),
    borrow: pick('--borrow', '#06B6D4'),
    build:  pick('--build',  '#C9A84C'),
    bi:     pick('--bi',     '#E8B96A'),
    buy:    pick('--buy',    '#EC4899'),
    text:   pick('--text',   '#FFFFFF'),
    t3:     pick('--t3',     'rgba(255,255,255,0.72)'),
    t4:     pick('--t4',     'rgba(255,255,255,0.48)'),
    line:   pick('--line',   'rgba(255,255,255,0.10)'),
  };
}

/**
 * Same data-sourcing pattern as Deconstruct.js. Primary: localStorage
 * 'reai_role'. Fallback: synthesise activities from analysis_mol_results,
 * using bot_score/build_score to approximate opportunity (0-100).
 */
function loadRole() {
  try {
    const stored = localStorage.getItem('reai_role');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed?.activities?.length) return parsed;
    }
  } catch { /* ignore */ }

  try {
    const raw = localStorage.getItem('analysis_mol_results');
    if (raw) {
      const mol = JSON.parse(raw);
      const r = (mol.roles || [])[0];
      if (r) {
        const tasks = r.task_breakdown || [];
        return {
          name: r.original_title || r.role_title || 'Role',
          function: localStorage.getItem('analysis_function') || '',
          hours: 40,
          activities: [
            {
              name: 'All tasks',
              hours: tasks.reduce((s, t) => s + (t.estimated_hours || 0), 0),
              subs: tasks.map((t) => {
                const bot = t.bot_score || t.automation_score || 0;
                const build = t.build_score || t.di_score || 0;
                const opportunity = Math.round(Math.max(bot, build * 0.8) * 100);
                return {
                  name: t.task,
                  hours: t.estimated_hours || 0,
                  deployment: t.category_4b || 'build',
                  opportunity,
                };
              }),
            },
          ],
        };
      }
    }
  } catch { /* ignore */ }

  return null;
}

// Deterministic x-drift so particles within the same score band don't overlap
function driftSeed(name) {
  const s = String(name || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

const EmptyState = ({ onGo }) => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, color: 'var(--text)', fontFamily: 'var(--fs)', position: 'relative', zIndex: 2 }}>
    <div className="eyebrow eye-teal">No role loaded</div>
    <p style={{ color: 'var(--t3)', fontSize: 14, maxWidth: 420, textAlign: 'center', lineHeight: 1.6 }}>
      Run Analysis first so sub-activities can be plotted on the opportunity spine.
    </p>
    <button className="btn-teal" onClick={onGo}>Go to Analysis →</button>
  </div>
);

const Analyze = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const stageRef = useRef(null);
  const rafRef = useRef(null);
  const hoverRef = useRef(null);
  const animStateRef = useRef({ t: 0 });

  const [activeStation, setActiveStation] = useState(null); // null = "all"
  const [selected, setSelected] = useState(null);           // { activityIdx, subIdx }
  const [, forceRender] = useState(0); // used to reflect hover changes without refs leaking

  // Global particles → ambient
  useEffect(() => {
    const api = window.__particles;
    if (!api) return;
    api.setMode('ambient');
    return () => api.setMode('ambient');
  }, []);

  const role = useMemo(() => loadRole(), []);

  // Flatten subs with their origin activity index for filtering + hit-testing
  const flatSubs = useMemo(() => {
    if (!role) return [];
    return (role.activities || []).flatMap((a, ai) =>
      (a.subs || []).map((s, si) => ({
        name: s.name,
        hours: s.hours || 0,
        deployment: s.deployment || 'build',
        opportunity: typeof s.opportunity === 'number' ? s.opportunity : 50,
        activityIdx: ai,
        subIdx: si,
        activityName: a.name,
      }))
    );
  }, [role]);

  const filteredSubs = useMemo(
    () => (activeStation == null ? flatSubs : flatSubs.filter((s) => s.activityIdx === activeStation)),
    [flatSubs, activeStation]
  );

  // Station counts
  const stationCounts = useMemo(() => {
    const c = {};
    flatSubs.forEach((s) => {
      c[s.activityIdx] = (c[s.activityIdx] || 0) + 1;
    });
    return c;
  }, [flatSubs]);

  // ── Canvas setup & draw loop ─────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    const stage = stageRef.current;
    if (!canvas || !stage) return;

    const ctx = canvas.getContext('2d');
    const colors = readBucketColors();
    let W = 0, H = 0, dpr = 1;

    const measure = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = stage.getBoundingClientRect();
      W = Math.max(240, rect.width);
      H = Math.max(240, rect.height);
      canvas.width = Math.floor(W * dpr);
      canvas.height = Math.floor(H * dpr);
      canvas.style.width = `${W}px`;
      canvas.style.height = `${H}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const yOf = (opportunity) => {
      const pad = 40;
      const usableH = H - pad * 2;
      return pad + (1 - Math.min(100, Math.max(0, opportunity)) / 100) * usableH;
    };

    const xOf = (sub) => {
      // Drift ±40px within a narrow centered band
      const drift = ((driftSeed(sub.name) % 80) - 40);
      return W / 2 + drift;
    };

    const rOf = (sub) => {
      const base = Math.sqrt(Math.max(1, sub.hours)) * 1.4;
      return Math.min(16, Math.max(8, base));
    };

    const positions = () =>
      filteredSubs.map((s) => ({
        s,
        x: xOf(s),
        y: yOf(s.opportunity),
        r: rOf(s),
        color: colors[s.deployment] || colors.build,
      }));

    const hitTest = (mx, my) => {
      const pts = positions();
      let best = null;
      let bestD2 = 20 * 20;
      for (const p of pts) {
        const dx = mx - p.x;
        const dy = my - p.y;
        const d2 = dx * dx + dy * dy;
        const hitR = Math.max(p.r + 4, 12);
        if (d2 < hitR * hitR && d2 < bestD2) {
          bestD2 = d2;
          best = p;
        }
      }
      return best;
    };

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const hit = hitTest(mx, my);
      const prev = hoverRef.current;
      const next = hit ? { activityIdx: hit.s.activityIdx, subIdx: hit.s.subIdx, x: mx, y: my } : null;
      if ((prev?.activityIdx !== next?.activityIdx) || (prev?.subIdx !== next?.subIdx)) {
        hoverRef.current = next;
        forceRender((n) => n + 1);
      } else if (next) {
        // Update cursor coords only (for label placement)
        hoverRef.current = next;
      }
      canvas.style.cursor = hit ? 'pointer' : 'default';
    };

    const onLeave = () => {
      if (hoverRef.current) {
        hoverRef.current = null;
        forceRender((n) => n + 1);
      }
      canvas.style.cursor = 'default';
    };

    const onClick = (e) => {
      const rect = canvas.getBoundingClientRect();
      const hit = hitTest(e.clientX - rect.left, e.clientY - rect.top);
      if (hit) {
        setSelected({ activityIdx: hit.s.activityIdx, subIdx: hit.s.subIdx });
      }
    };

    const draw = (now) => {
      animStateRef.current.t = now / 1000;
      const t = animStateRef.current.t;
      ctx.clearRect(0, 0, W, H);

      const pts = positions();

      // ── Station band (horizontal teal tint over the active activity's Y range)
      if (activeStation != null && pts.length > 0) {
        const ys = pts.map((p) => p.y);
        const rs = pts.map((p) => p.r);
        const minY = Math.min(...ys.map((y, i) => y - rs[i] - 6));
        const maxY = Math.max(...ys.map((y, i) => y + rs[i] + 6));
        ctx.fillStyle = 'rgba(0,212,200,0.04)';
        ctx.fillRect(0, minY, W, maxY - minY);
      }

      // ── Spine (vertical line centre) — brighter
      ctx.strokeStyle = 'rgba(255,255,255,0.25)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(W / 2, 20);
      ctx.lineTo(W / 2, H - 20);
      ctx.stroke();

      // ── Ticks + axis labels (left side only, 8px inward toward spine)
      ctx.fillStyle = colors.t4;
      ctx.font = "9px 'Overpass Mono', monospace";
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      [0, 25, 50, 75, 100].forEach((v) => {
        const y = yOf(v);
        ctx.strokeStyle = 'rgba(255,255,255,0.25)';
        ctx.beginPath();
        ctx.moveTo(W / 2 - 8, y);
        ctx.lineTo(W / 2, y);
        ctx.stroke();
        ctx.fillText(String(v), W / 2 - 12, y);
      });

      // "OPPORTUNITY" axis label at bottom
      ctx.textAlign = 'center';
      ctx.fillStyle = colors.t4;
      ctx.fillText('OPPORTUNITY  ━━━━━━━━▸', W / 2, H - 10);

      // ── Hover crosshair (drawn under particles so dots sit on top)
      const hover = hoverRef.current;
      if (hover) {
        const hp = pts.find((q) => q.s.activityIdx === hover.activityIdx && q.s.subIdx === hover.subIdx);
        if (hp) {
          ctx.strokeStyle = 'rgba(255,255,255,0.1)';
          ctx.lineWidth = 1;
          ctx.setLineDash([4, 4]);
          ctx.beginPath();
          ctx.moveTo(0, hp.y);
          ctx.lineTo(W, hp.y);
          ctx.stroke();
          ctx.setLineDash([]);
          // Live opportunity score on the left axis at the hover Y
          ctx.fillStyle = colors.text;
          ctx.font = "600 10px 'Overpass Mono', monospace";
          ctx.textAlign = 'right';
          ctx.textBaseline = 'middle';
          ctx.fillText(String(Math.round(hp.s.opportunity)), W / 2 - 12, hp.y);
        }
      }

      // ── Particles
      const sel = selected;
      pts.forEach((p) => {
        const isHover = hover && hover.activityIdx === p.s.activityIdx && hover.subIdx === p.s.subIdx;
        const isSel   = sel   && sel.activityIdx   === p.s.activityIdx && sel.subIdx   === p.s.subIdx;

        const pulse = isHover ? 1.4 + Math.sin(t * 6) * 0.08 : 1;
        const radius = p.r * pulse;

        // Soft halo
        ctx.fillStyle = p.color + '33'; // ~20% alpha (works with 6-digit hex tokens)
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius * 2.2, 0, Math.PI * 2);
        ctx.fill();

        // Main dot
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
        ctx.fill();

        // Outer ring (stroke only at radius + 4, ~60% alpha)
        ctx.strokeStyle = p.color + '99';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, radius + 4, 0, Math.PI * 2);
        ctx.stroke();

        // Bright inner white highlight (2px) — always visible center anchor
        ctx.fillStyle = '#FFFFFF';
        ctx.beginPath();
        ctx.arc(p.x, p.y, 2, 0, Math.PI * 2);
        ctx.fill();

        // Pulsing hover ring (second outer ring that breathes)
        if (isHover) {
          const pulseR = radius + 4 + Math.sin(t * 6) * 4 + 6;
          ctx.strokeStyle = p.color;
          ctx.globalAlpha = 0.5 + Math.sin(t * 6) * 0.3;
          ctx.lineWidth = 1.25;
          ctx.beginPath();
          ctx.arc(p.x, p.y, pulseR, 0, Math.PI * 2);
          ctx.stroke();
          ctx.globalAlpha = 1;
        }

        // Selection ring
        if (isSel) {
          ctx.strokeStyle = p.color;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(p.x, p.y, radius + 7, 0, Math.PI * 2);
          ctx.stroke();
        }
      });

      // ── Hover name label (on top of everything)
      if (hover) {
        const p = pts.find((q) => q.s.activityIdx === hover.activityIdx && q.s.subIdx === hover.subIdx);
        if (p) {
          const text = p.s.name.length > 40 ? p.s.name.slice(0, 40) + '…' : p.s.name;
          ctx.font = "11px 'Manrope', sans-serif";
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          const w = ctx.measureText(text).width + 12;
          let tx = p.x + 18;
          if (tx + w > W - 8) tx = p.x - 18 - w;
          const ty = p.y;
          ctx.fillStyle = 'rgba(10,10,26,0.9)';
          ctx.fillRect(tx - 4, ty - 10, w, 20);
          ctx.strokeStyle = p.color;
          ctx.lineWidth = 1;
          ctx.strokeRect(tx - 4, ty - 10, w, 20);
          ctx.fillStyle = colors.text;
          ctx.fillText(text, tx + 2, ty);
        }
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    const ro = new ResizeObserver(() => {
      requestAnimationFrame(() => measure());
    });
    ro.observe(stage);
    measure();
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);
    canvas.addEventListener('click', onClick);
    rafRef.current = requestAnimationFrame(draw);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
      canvas.removeEventListener('click', onClick);
    };
  }, [filteredSubs, selected]);

  const handleReset = () => {
    localStorage.removeItem('reai_role');
    navigate('/analysis');
  };

  if (!role) return <EmptyState onGo={() => navigate('/analysis')} />;

  const activities = role.activities || [];
  const totalSubs = flatSubs.length;
  const selectedSub = selected
    ? flatSubs.find((s) => s.activityIdx === selected.activityIdx && s.subIdx === selected.subIdx)
    : null;

  return (
    <div style={{
      height: '100vh',
      overflow: 'hidden',
      color: 'var(--text)',
      fontFamily: 'var(--fs)',
      position: 'relative',
      zIndex: 2,
      paddingTop: 'var(--nav-h)',
    }}>
      {/* ── Top nav ────────────────────────────────────── */}
      <header className="nav">
        <span className="nav-brand">re<span className="ai">AI</span>magination</span>
        <span className="nav-sep" />
        <span className="nav-lbl">Product flow</span>
        <nav className="nav-pages" style={{ marginLeft: 'auto' }}>
          <button onClick={() => navigate('/signal-room')}     className="np">Signal Room</button>
          <button onClick={() => navigate('/intelligence')}    className="np">Intelligence</button>
          <button onClick={() => navigate('/blueprint/cockpit')} className="np">Blueprint</button>
          <button onClick={() => navigate('/stage')}           className="np">Stage</button>
          <button onClick={() => navigate('/showcase/ai-tools')} className="np">Showcase</button>
        </nav>
      </header>

      {/* ── Chrome bar · 5-step stepper + role chip ───── */}
      <div className="chro-bar">
        <span className="cb-brand">re<span className="ai">AI</span>magination</span>
        <div className="cb-tabs">
          {STEPS.map((s) => (
            <button
              key={s.path}
              className={`ct${s.active ? ' on' : ''}`}
              onClick={() => !s.active && navigate(s.path)}
              style={{ opacity: s.done || s.active ? 1 : 0.6 }}
            >
              <span style={{ marginRight: 6, color: s.done ? 'var(--teal)' : 'inherit' }}>{s.token}</span>
              {s.label}
            </button>
          ))}
        </div>
        <div className="cb-right">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--t2)' }}>
            <span className="act-dot" style={{ background: 'var(--teal)', boxShadow: '0 0 5px var(--teal)' }} />
            {role.name}
          </span>
          <button
            onClick={handleReset}
            style={{
              padding: '4px 10px', fontSize: 10, fontFamily: 'var(--fm)',
              color: 'var(--t3)', background: 'transparent',
              border: '1px solid var(--line)', borderRadius: 'var(--r-sm)',
              cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.12em',
            }}
          >
            Reset
          </button>
        </div>
      </div>

      <HintBar screenId="analyze" hints={HINTS} />

      {/* ── 3-col analyze grid ──────────────────────── */}
      <div
        className="analyze-main"
        style={{
          height: 'calc(100vh - var(--nav-h) - var(--chro-h) - var(--hint-h, 0px) - var(--status-h))',
          overflow: 'hidden',
        }}
      >
        {/* Left — station filter rows */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, overflowY: 'auto', paddingRight: 4 }}>
          <div className="eyebrow" style={{ marginBottom: 4 }}>Stations · {activities.length}</div>
          <div
            className={`station-row${activeStation == null ? ' on' : ''}`}
            onClick={() => setActiveStation(null)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setActiveStation(null)}
          >
            <span className="station-name">All sub-activities</span>
            <span className="station-count">{totalSubs}</span>
          </div>
          {activities.map((a, ai) => (
            <div
              key={ai}
              className={`station-row${activeStation === ai ? ' on' : ''}`}
              onClick={() => setActiveStation(ai)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setActiveStation(ai)}
            >
              <span className="station-name">{a.name}</span>
              <span className="station-count">{stationCounts[ai] || 0}</span>
            </div>
          ))}
        </div>

        {/* Centre — spine canvas + hint */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
          gap: 8,
          height: '100%',
        }}>
          <div
            className="spine-stage"
            ref={stageRef}
            style={{
              position: 'relative',
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              background: 'rgba(10,10,26,0.08)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 'var(--r-lg)',
            }}
          >
            <canvas
              id="spine-cv"
              ref={canvasRef}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                display: 'block',
              }}
            />
          </div>
        </div>

        {/* Right — inspector */}
        <div className="inspector">
          <div className="insp-hd">Selected</div>
          {selectedSub ? (
            <>
              <div className="insp-name">{selectedSub.name}</div>
              <div className="insp-meta">{selectedSub.activityName}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                <span className="act-dot" style={{ background: `var(--${selectedSub.deployment})`, boxShadow: `0 0 5px var(--${selectedSub.deployment})` }} />
                <span style={{ fontFamily: 'var(--fm)', fontSize: 10, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.14em' }}>
                  4B · {BUCKET_LABEL[selectedSub.deployment] || selectedSub.deployment}
                </span>
              </div>
              <div className="pstats" style={{ marginTop: 12 }}>
                <div className="pstat">
                  <div className="pstat-l">Hours</div>
                  <div className="pstat-v">{selectedSub.hours}</div>
                </div>
                <div className="pstat">
                  <div className="pstat-l">Opp.</div>
                  <div className="pstat-v">{selectedSub.opportunity}</div>
                </div>
                <div className="pstat">
                  <div className="pstat-l">Bucket</div>
                  <div className="pstat-v" style={{ fontSize: 13 }}>{(BUCKET_LABEL[selectedSub.deployment] || selectedSub.deployment).toUpperCase()}</div>
                </div>
              </div>
              <button
                className="btn-teal"
                onClick={() => navigate('/sort')}
                style={{ marginTop: 14 }}
              >
                Re-route →
              </button>
            </>
          ) : (
            <>
              <div className="insp-name" style={{ color: 'var(--t3)' }}>Nothing selected</div>
              <div className="insp-meta">Click a particle on the spine to inspect.</div>
              <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {BUCKET_KEYS.map((k) => (
                  <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--t3)' }}>
                    <span className="act-dot" style={{ background: `var(--${k})` }} />
                    {BUCKET_LABEL[k]}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Status bar ───────────────────────────────── */}
      <div className="status-bar">
        <span className="st-live">● Live</span>
        <span className="st-sep">·</span>
        <span>session</span>
        <span className="st-sep">·</span>
        <span>analyze</span>
        <span className="st-sep">·</span>
        <span>spine</span>
        <span className="st-sep">·</span>
        <span>particles · {filteredSubs.length}{activeStation != null ? ` / ${totalSubs}` : ''}</span>
      </div>
    </div>
  );
};

export default Analyze;
